import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store';
import { useSupabaseStore } from '@/store/SupabaseStore'; // Import useSupabaseStore
import { APP_VERSION } from '@/utils/version';
import { 
  MessageSquare, Settings, Users, Cpu, Code, PanelLeft, 
  Plus, User, Layout, Library, Image, Brain, Bot, 
  GraduationCap, Microscope, Beaker, Loader2, Bug, 
  MessagesSquare,
  Map as BlueprintIcon,
  FolderKanban
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import BugReportModal from '@/components/feedback/BugReportModal';
import { getNavItems } from '@/lib/navigation'; // Import getNavItems
import BrandLogo from '@/components/common/BrandLogo';
import { useSubscription } from '@/hooks/useSubscription';

const isValidUUID = (uuid: string): boolean => {
  if (!uuid) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

interface SidebarProps {
  collapsed?: boolean;
  setCollapsed?: (collapsed: boolean) => void;
}

const Sidebar = ({ collapsed = false, setCollapsed }: SidebarProps) => {
  const [showBugReport, setShowBugReport] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user: appUser } = useAppStore();
  const { addChat, models } = useSupabaseStore();
  const { user: authUser } = useAuth();
  const { profile } = useProfile();
  const { currentTier } = useSubscription();

  const [showChatHistory, setShowChatHistory] = useState(false);
  const [showCanvasHistory, setShowCanvasHistory] = useState(false);
  const [openCollapsibles, setOpenCollapsibles] = useState<Record<string, boolean>>({}); // Use a map for multiple collapsibles
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  const handleCollapsedChange = useCallback(() => {
    if (setCollapsed) {
      setCollapsed(!collapsed);
    }
  }, [collapsed, setCollapsed]);

  useEffect(() => {
    // Initialize collapsible state based on current path
    const initialOpenState: Record<string, boolean> = {};
    getNavItems(location.pathname, setShowBugReport).forEach(item => {
      if (item.hasChildren && item.children?.some(child => location.pathname.startsWith(child.path))) {
        initialOpenState[item.path] = true;
      }
    });
    setOpenCollapsibles(initialOpenState);

    setPrevPathname(location.pathname);
  }, [location.pathname]);

  const handleCollapsibleToggle = useCallback((path: string) => {
    setOpenCollapsibles(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  }, []);

  const defaultModel = useMemo(() => {
    if (!models || models.length === 0) { // Add check for models being undefined or empty
      return undefined; // Or a default fallback if appropriate
    }
    if (appUser && appUser.defaultModelApiName) { // Use defaultModelApiName
      const found = models.find(m => m.apiName === appUser.defaultModelApiName); // Find by apiName
      if (found) return found;
    }
    return models.find(m => m.tier === 'free') || models[0];
  }, [models, appUser]);

  const handleNewChat = useCallback(async () => {
    if (!appUser || !defaultModel || isCreatingChat) return;

    setIsCreatingChat(true);
    try {
      const newChat = await addChat(appUser.id, 'New Chat', defaultModel.id);
      if (!newChat || !newChat.id) throw new Error("No chat ID returned");
      navigate(`/chat/${newChat.id}`);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create a new chat. Please try again.",
      });
    } finally {
      setIsCreatingChat(false);
    }
  }, [appUser, defaultModel, addChat, navigate, isCreatingChat]);

  const navItems = useMemo(() => {
    const allNavItems = getNavItems(location.pathname, setShowBugReport);
    const sidebarItems = allNavItems.filter(item =>
      !['/about', '/news', '/purchase', '/forums', '/lobby/general', '/docs'].includes(item.path)
    );
    
    return sidebarItems.filter(item => {

      // Always show Access Control for admins
      if (item.path === '/access-control' && (profile?.role || '').toLowerCase() === 'admin') {
        return true;
      }
      if (!item.requiredTier) return true;
      return item.requiredTier.includes(currentTier);
    });
  }, [location.pathname, setShowBugReport, currentTier, profile?.role]);

  const displayName = profile?.name || "User"; // Changed this line
  const displayEmail = authUser?.email || "";
  const avatarUrl = profile?.avatar || appUser?.avatar;
  const currentVersion = 'v' + APP_VERSION;

  return (
    <div className={`hidden md:flex bg-sidebar text-sidebar-foreground flex-col ${collapsed ? 'w-16' : 'w-64'} transition-all duration-300 border-r border-sidebar-border h-full relative overflow-hidden`}>
      <div className="p-3 flex items-center justify-between flex-shrink-0">
        {!collapsed && (
          <h1 className="text-xl font-bold flex flex-col items-start gap-1">
            <div className="flex items-center gap-2">
              <BrandLogo size={24} />
              <Link
                to="/changelog"
                className="text-xs text-muted-foreground mt-0.5 hover:underline"
                style={{ letterSpacing: '0.03em' }}
              >
                {currentVersion}
              </Link>
            </div>
            <span className="text-[10px] text-muted-foreground uppercase ml-[34px] tracking-wide">
              alpha
            </span>
          </h1>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCollapsedChange}
          className="text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <PanelLeft size={20} className={collapsed ? 'rotate-180' : ''} />
        </Button>
      </div>

      <div className="p-3">
        <Button
          className="w-full gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
          onClick={handleNewChat}
          type="button"
          disabled={isCreatingChat}
        >
          {isCreatingChat ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
          {!collapsed && (isCreatingChat ? "Creating..." : "New Chat")}
        </Button>
      </div>

      <ScrollArea className="flex-1 p-3 overflow-x-hidden">
        <div className="space-y-1">
          {navItems.map((item) => (
            <div key={item.path} className="mb-1">
              {item.hasChildren ? (
                <Collapsible
                  open={openCollapsibles[item.path]}
                  onOpenChange={(isOpen) =>
                    setOpenCollapsibles((prev) => ({ ...prev, [item.path]: isOpen }))
                  }
                  className="w-full"
                >
                  <CollapsibleTrigger
                    className="w-full"
                    onClick={() => {
                      // If the sidebar is collapsed, expand it so children can be shown
                      if (collapsed && setCollapsed) {
                        setCollapsed(false);
                      }
                    }}
                  >
                    <div
                      className={`flex items-center gap-2 rounded-md px-3 py-2 w-full transition-colors cursor-pointer ${
                        item.active
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'hover:bg-sidebar-accent/50'
                      }`}
                    >
                      {item.icon}
                      {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
                    </div>
                  </CollapsibleTrigger>
                  {!collapsed && (
                    <CollapsibleContent className="pl-8 pt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                            child.active
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                              : 'hover:bg-sidebar-accent/50'
                          }`}
                        >
                          {child.icon}
                          <span>{child.label}</span>
                        </Link>
                      ))}
                    </CollapsibleContent>
                  )}
                </Collapsible>
              ) : (
                <Link
                  to={item.path}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 transition-colors ${
                    item.active
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'hover:bg-sidebar-accent/50'
                  }`}
                >
                  {item.icon}
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="border-t border-sidebar-border py-0.5 px-3">
        <div className="flex items-center gap-3 mb-2">
          {authUser && (
            <>
              {!collapsed && (
                <div className="flex-1 truncate">
                  <p className="font-medium text-sm">{displayName}</p>
                  <p className="text-xs text-sidebar-foreground/60">{displayEmail}</p>
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full hover:bg-sidebar-accent"
                asChild
              >
                <Link to="/profile">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={avatarUrl} alt={displayName} />
                    <AvatarFallback>
                      <Bot size={20} />
                    </AvatarFallback>
                  </Avatar>
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {showBugReport && (
        <BugReportModal open={showBugReport} onOpenChange={setShowBugReport} />
      )}
    </div>
  );
};

export default Sidebar;